import React from 'react';

export interface StatCardProps {
    /** Microlabel above the value — rendered uppercase, so pass it in sentence case. */
    label: React.ReactNode;
    /** Pre-formatted value. Formatting stays with the caller; the card only styles it. */
    value: React.ReactNode;
    /** Optional third line: a caller-supplied delta node (arrow + its own status hue). */
    delta?: React.ReactNode;
    /** Optional third line: a muted sub-caption. Ignored when `delta` is given. */
    caption?: React.ReactNode;
    /** Token class overriding the value colour, e.g. `text-risk` or `text-safe`. */
    valueColorClassName?: string;
    /** Custom CSS color (hex/rgb/etc) for palette-based colors that don't have design tokens. */
    valueColor?: string;
    /** `hero` is the lead-tile treatment: accent border on nav-active fill. */
    variant?: 'default' | 'hero';
    testId?: string;
    className?: string;
}

const WRAPPER_BASE = 'rounded-[14px] px-4 py-3';

const VARIANT_WRAPPER: Record<'default' | 'hero', string> = {
    default: 'bg-surface border border-line',
    hero: 'bg-nav-active border-[1.5px] border-accent',
};

const VARIANT_VALUE_COLOR: Record<'default' | 'hero', string> = {
    default: 'text-ink',
    hero: 'text-accent-active',
};

/**
 * The one shared KPI tile (bd deepcontext-07em, Style C): uppercase microlabel,
 * bold tabular value, and an optional third line that is either a delta the
 * caller styles or a muted caption. Every colour comes from a design token, so
 * the tile follows light/dark without a per-page override.
 *
 * The `hero` variant is the lead-tile treatment lifted out of JobPerformance's
 * BidReqsKpiTile — a 1.5px accent border on the nav-active fill, with the value
 * in accent-active unless the caller overrides it.
 */
export const StatCard: React.FC<StatCardProps> = ({
    label,
    value,
    delta,
    caption,
    valueColorClassName,
    valueColor,
    variant = 'default',
    testId,
    className = '',
}) => (
    <div
        data-testid={testId}
        className={`${WRAPPER_BASE} ${VARIANT_WRAPPER[variant]} ${className}`.trim()}
    >
        <div className="text-[10px] font-semibold uppercase tracking-wide text-ink-muted">{label}</div>
        <div
            className={`text-xl font-bold tabular-nums ${valueColorClassName || VARIANT_VALUE_COLOR[variant]}`}
            style={valueColor ? { color: valueColor } : undefined}
        >
            {value}
        </div>
        {delta ? (
            <div className="mt-0.5 text-[11px]">{delta}</div>
        ) : caption ? (
            <div data-testid="stat-card-caption" className="mt-0.5 text-[11px] text-ink-muted">
                {caption}
            </div>
        ) : null}
    </div>
);

export default StatCard;
